import { orderChatDeliveries } from "@shared/chatDelivery";
import { includeInBuildModelHistory } from '@shared/canvasUIAction';
import {reconcileRecoveredRunCards} from './recovered-run-cards';
import {withoutProgressPollEvidence} from './concept-progress-liveness';
import { nativeAgentTurnMetadata } from '@shared/nativeAgentTranscript';
// client/src/pages/canvas/conversation-hydrate.ts
// v7.43 — ONE pure hydration path for a task's saved conversation blob.
//
// Before this file the initial-load path (loadOrCreateProject) and the
// switchProject path each carried their own copy of the display filter and
// they had drifted: switchProject still rendered the "(Previous turn didn't
// complete)" marker for EVERY contentless assistant entry, including the
// mid-history tool_calls-only turns of successful runs that v7.31 had
// already fixed on the initial-load side. Both call sites now go through
// hydrateConversation().
//
// PURE by design (no React, no store, no fetch) so scripts/assert-run-memory.ts
// can import it under tsx.
//
// Rules
// ─────
// Entries WITHOUT conceptsRun keep today's (v7.31) behaviour exactly:
//   • only user + assistant roles are displayable (tool results are agent
//     loop internals);
//   • the "STOP — your previous response narrated" forcing reminder (a user
//     turn) and the assistant entry right before it are hidden;
//   • a user entry with no text is dropped;
//   • a contentless assistant entry in the MIDDLE of the history is dropped
//     unless it carries UI state to render (cards / pills / task groups…);
//   • a contentless assistant entry that is the LAST raw entry is a genuinely
//     crashed turn → its content becomes the faded CRASH_MARKER;
//   • everything else passes through.
//
// Entries WITH conceptsRun (server-owned run memory) are ALWAYS kept:
//   • status 'generating' → content '', isStreaming true, agentThinking
//     CONCEPTS_STILL_WRITING (a live card the store's watchAngleSetRun poll
//     will flip once the run resolves) — never the crash marker;
//   • status 'ready' | 'failed' | 'interrupted' → pass through as-is. The
//     server already wrote the outcome text, suggestionPills and
//     angleSetCard onto the entry; the chat renders them like any other
//     finished assistant turn.

import type { AgentTaskGroup, CanvasChatMessage } from './canvas-types';

export const CRASH_MARKER =
  "_(Previous turn didn't complete — the agent crashed or the stream was interrupted. Resend your last message to retry.)_";

export const CONCEPTS_STILL_WRITING = 'Still writing your concepts…';

/** A raw entry from the conversation blob. Anything the client OR the server
 *  ever persisted — OpenAI-format tool turns included — so every field is
 *  unknown until checked. */
export type RawConversationEntry = { [key: string]: unknown };

const hasText = (c: unknown): c is string => typeof c === 'string' && c.trim() !== '';

/** Persisted provider-format messages can carry text beside image_url blocks. */
export function conversationText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  return content.flatMap(part => part && typeof part === 'object' && part.type === 'text' && typeof part.text === 'string' ? [part.text] : []).join('\n');
}

function conversationImages(content: unknown): string[] {
  if (!Array.isArray(content)) return [];
  return content.flatMap(part => {
    const url = part?.type === 'image_url' ? (typeof part.image_url === 'string' ? part.image_url : part.image_url?.url) : undefined;
    return typeof url === 'string' && /^https?:\/\//i.test(url) ? [url] : [];
  });
}

const isStopReminder = (m: RawConversationEntry | undefined): boolean =>
  !!m && m.role === 'user' && typeof m.content === 'string' && m.content.startsWith('STOP — your previous response narrated');

const conceptsRunOf = (h: RawConversationEntry): CanvasChatMessage['conceptsRun'] | undefined => {
  const cr = h.conceptsRun as CanvasChatMessage['conceptsRun'] | undefined;
  return cr && typeof cr === 'object' && typeof cr.status === 'string' ? cr : undefined;
};

/** Does a contentless assistant entry still have something to render? An
 *  entry with conceptsRun always does (the run card). */
export function carriesUI(h: RawConversationEntry): boolean {
  return !!(
    h.playbookProgress || h.adCards || h.imageSlots || h.agentTaskGroups || h.workflowTaskCards ||
    h.toolResults || h.suggestionPills || h.angleSetCard || h.classifierReasoning || conceptsRunOf(h)
  );
}

/** The display filter: which raw entries become chat bubbles. Index-aware
 *  (the dangling-vs-mid-history rule looks at position in the RAW blob). */
export function selectDisplayable(history: ReadonlyArray<RawConversationEntry>): RawConversationEntry[] {
  return history.filter((h, i) => {
    if (!h || (h.role !== 'user' && h.role !== 'assistant')) return false;
    // v7.31 — hide the hallucination-guard internals older histories persisted.
    if (isStopReminder(h)) return false;
    if (h.role === 'assistant' && typeof h.content === 'string' && isStopReminder(history[i + 1])) return false;
    // v7.43 — server-owned run memory is always displayable.
    if (conceptsRunOf(h) || nativeAgentTurnMetadata(h)) return true;
    // User messages: drop only if truly empty.
    if (h.role === 'user' && !hasText(conversationText(h.content)) && !extractThumbnails(h)?.length) return false;
    // v7.31 — contentless assistant entries are the agent loop's tool_calls-
    // only state. Only a DANGLING one (last raw entry) is a crashed turn
    // worth a marker; mid-history ones are dropped unless they carry UI.
    if (h.role === 'assistant' && !hasText(h.content) && i < history.length - 1) {
      if (!carriesUI(h)) return false;
    }
    return true;
  });
}

/** Thumbnails for old role+content-only user records that embedded images
 *  as "[Attached image: url]" text. */
function extractThumbnails(h: RawConversationEntry): string[] | undefined {
  const saved = Array.isArray(h.thumbnails) ? (h.thumbnails as string[]) : [];
  if (saved.length > 0) return saved;
  if (h.role !== 'user') return undefined;
  const out: string[] = conversationImages(h.content);
  const imgRe = /\[Attached image: (https?:\/\/[^\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = imgRe.exec(conversationText(h.content))) !== null) { if (m[1]) out.push(m[1]); }
  return out.length > 0 ? Array.from(new Set(out)) : undefined;
}

/** Raw blob → renderable chat messages. See the header for the rules. */
export function hydrateConversation(history: ReadonlyArray<RawConversationEntry>): CanvasChatMessage[] {
  const displayable = orderChatDeliveries(selectDisplayable(history));
  const stamp = Date.now();
  return reconcileRecoveredRunCards(displayable.map((raw, i) => {
    const h=withoutProgressPollEvidence(raw);
    const id = typeof h.id === 'string' && h.id ? h.id : `restored-${i}-${stamp}`;
    const thumbnails = extractThumbnails(h);
    const cr = conceptsRunOf(h);
    const nativeTurn = nativeAgentTurnMetadata(h);

    if (nativeTurn) {
      // This is server-owned desktop work, not a disconnected web stream.
      // The active chat reads its canonical result; never offer a paid replay.
      const content = conversationText(h.content);
      const fallback = nativeTurn.status === 'completed' ? 'Desktop task finished.'
        : nativeTurn.status === 'stopped' ? 'Desktop task stopped.'
          : nativeTurn.status === 'failed' ? 'The desktop task could not finish.' : '';
      return {
        ...(h as unknown as CanvasChatMessage), id, thumbnails,
        content: content || (h.role === 'assistant' ? fallback : ''),
        isStreaming: false, agentThinking: undefined, classifierReasoning: undefined,
      };
    }

    if (cr) {
      if (cr.status === 'generating') {
        // Live card: the run is (or was, until the server died) still
        // writing. watchAngleSetRun resolves it; NEVER the crash marker.
        return {
          ...(h as unknown as CanvasChatMessage),
          id,
          content: '',
          isStreaming: true,
          conceptRunVerifiedAt: undefined,
          agentThinking: CONCEPTS_STILL_WRITING,
          thumbnails,
        };
      }
      // ready | failed | interrupted — the server wrote the outcome. Pass
      // through; only guard the renderer against a non-string content.
      return {
        ...(h as unknown as CanvasChatMessage),
        id,
        content: typeof h.content === 'string' ? h.content : '',
        thumbnails,
      };
    }

    // Legacy: an assistant entry that came back empty is a crashed turn —
    // substitute the faded marker so the chat shows a visible gap instead
    // of a blank bubble.
    const content =
      h.role === 'assistant' && !hasText(h.content)
        ? CRASH_MARKER
        : conversationText(h.content);
    return {
      ...(h as unknown as CanvasChatMessage),
      id,
      content,
      thumbnails,
    };
  }));
}

/** The role+content pairs handed back to the LLM as context (the server
 *  rebuilds the full tool-call thread itself). Built from the SAME
 *  displayable set as the chat, on the RAW content — the crash marker and
 *  the "still writing" placeholder never reach the model. */
export function toLlmHistory(
  history: ReadonlyArray<RawConversationEntry>,
): Array<{ role: 'user' | 'assistant'; content: string }> {
  return selectDisplayable(history)
    .filter(h => includeInBuildModelHistory(h))
    // A contentless assistant row (a live concepts-run card) renders in the
    // chat but is loop state, not context — the server strips the same shape
    // from the model-bound thread; mirrored here for the DB-empty fallback.
    .filter((h) => !(h.role === 'assistant' && !hasText(h.content)))
    .map((h) => ({
      role: h.role as 'user' | 'assistant',
      content: [conversationText(h.content), ...conversationImages(h.content).map(url => `[Attached image: ${url}]`)].filter(Boolean).join('\n'),
    }));
}

/** v7.43 — settle a persisted run card once the run's outcome is known
 *  client-side (watchAngleSetRun synthesized the result because the server
 *  died between saving the set and writing its entry). 'completed' marks every
 *  step success; 'failed' turns the running step red and leaves the untouched
 *  ones hollow. Tolerates the server-side 'completed' step spelling too. */
export function settleTaskGroups(
  groups: AgentTaskGroup[] | undefined,
  outcome: 'completed' | 'failed',
): AgentTaskGroup[] | undefined {
  if (!groups) return groups;
  return groups.map((g) => ({
    ...g,
    status: g.status === 'completed' ? 'completed' : outcome,
    steps: g.steps.map((s) => {
      const st = s.status as string;
      if (st === 'success' || st === 'completed') return { ...s, status: 'success' as const };
      if (outcome === 'completed') return { ...s, status: 'success' as const };
      return st === 'running' ? { ...s, status: 'error' as const } : s;
    }),
  }));
}

/** Recover only the late brief tile; never overwrite live thumbnails, progress or newer messages. */
export function recoverCompletedBriefCards(current: CanvasChatMessage[], history: ReadonlyArray<RawConversationEntry>): CanvasChatMessage[] {
  const saved = new Map(hydrateConversation(history).filter(m => m.angleSetCard).map(m => [m.id, m]));
  return current.map(message => {
    const entry = saved.get(message.id);
    return !message.angleSetCard && message.conceptsRun?.autoBuild && entry?.angleSetCard
      && message.conceptsRun.setId === entry.angleSetCard.setId
      ? { ...message, angleSetCard: entry.angleSetCard } : message;
  });
}
